import { Controller } from "@hotwired/stimulus"

/**
 * IoT Chart Controller
 *
 * Wraps Chart.js for dashboard visualizations. Accepts data and options
 * as JSON via data-values and renders into a <canvas> element.
 *
 * Usage:
 *   <canvas
 *     data-controller="iot--chart"
 *     data-iot--chart-type-value="bar"
 *     data-iot--chart-data-value="<%= chart_data.to_json %>"
 *     data-iot--chart-options-value='{"plugins":{"legend":{"display":false}}}'
 *   ></canvas>
 */
export default class extends Controller {
  static values = {
    type: { type: String, default: "bar" },
    data: { type: Object, default: {} },
    options: { type: Object, default: {} },
  }

  async connect() {
    // Lazy-load Chart.js so it doesn't bloat the initial bundle
    const { Chart, registerables } = await import("chart.js")
    Chart.register(...registerables)
    this.Chart = Chart

    this._renderChart()
  }

  disconnect() {
    if (this._chart) {
      this._chart.destroy()
      this._chart = null
    }
  }

  dataValueChanged() {
    if (this._chart) {
      this._chart.data = this.dataValue
      this._chart.update()
    }
  }

  _renderChart() {
    if (this._chart) {
      this._chart.destroy()
    }

    const defaultOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            boxWidth: 12,
            padding: 12,
            font: { size: 11 },
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
        },
      },
    }

    // Deep merge default options with caller-provided options
    const mergedOptions = this._mergeDeep(defaultOptions, this.optionsValue)

    this._chart = new this.Chart(this.element, {
      type: this.typeValue,
      data: this.dataValue,
      options: mergedOptions,
    })
  }

  /**
   * Simple deep-merge utility — plain objects only, no arrays.
   */
  _mergeDeep(target, source) {
    const output = { ...target }
    if (this._isObject(target) && this._isObject(source)) {
      Object.keys(source).forEach((key) => {
        if (this._isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] })
          } else {
            output[key] = this._mergeDeep(target[key], source[key])
          }
        } else {
          Object.assign(output, { [key]: source[key] })
        }
      })
    }
    return output
  }

  _isObject(item) {
    return item && typeof item === "object" && !Array.isArray(item)
  }
}
